import { queryOptions, useMutation } from '@tanstack/react-query'
import { getNotifier } from '../api/index.js'
import { cmsAssetsService, type ImageAssetScope, type VideoAssetScope } from '../services/cmsAssetsService.js'

/**
 * Uploads and change history, for every CMS surface.
 *
 * `MediaImageField`, `MediaVideoField` and `ChangeHistoryPanel` are shared
 * components; these are the hooks behind them. Nothing here knows what kind of
 * record is being edited — the caller's `scope` / `table` says that.
 */

export const cmsHistoryKeys = {
  all: ['cmsHistory'] as const,
  row: (table: string, rowId: string) => ['cmsHistory', table, rowId] as const,
}

export const cmsHistoryOptions = (table: string, rowId: string) =>
  queryOptions({
    queryKey: cmsHistoryKeys.row(table, rowId),
    queryFn: () => cmsAssetsService.history(table, rowId),
    enabled: !!rowId,
  })

function message(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

export function useUploadCmsImage() {
  return useMutation({
    mutationFn: ({ file, scope }: { file: File; scope: ImageAssetScope }) =>
      cmsAssetsService.uploadImage(file, scope),
    onError: (error) => getNotifier().error(message(error, 'Failed to upload image')),
  })
}

export function useUploadCmsVideo() {
  return useMutation({
    mutationFn: ({
      file,
      scope,
      onProgress,
    }: {
      file: File
      scope: VideoAssetScope
      onProgress?: (percent: number) => void
    }) => cmsAssetsService.uploadVideo(file, scope, onProgress),
    onError: (error) => getNotifier().error(message(error, 'Failed to upload video')),
  })
}
